import "./share.css"
import { AppText } from "~/ui/app-text"
import { useState, useEffect, useRef, SetStateAction, Dispatch, ReactNode } from "react"
import { isAndroid, isIOS, isMobile, isTwitterApp } from "~/common/platform"

import { PhoneNumberValue } from "~/common/phone-number"
import { getMetaContentMaybe } from "~/common/get-meta-content"
import { cn } from "~/common/cn"
import { useThemeColor } from "~/hooks/use-theme-color"

import { useKeyboardVisibility } from "~/hooks/use-keyboard-visibility"
import { Logo } from "~/landing/components/logo"
import { TextureGrainBackground } from "./components/texture-grain-background"
import { JoinBar } from "./components/join-bar"
import { ChatShareMessage } from "./components/message/chat-share-message"
import { getInviterData, getMessageData } from "~/share/share-embedded-data"

type ShareScreenBaseProps = {
  renderContent: () => ReactNode
  setPhoneValue: Dispatch<SetStateAction<PhoneNumberValue>>
  phoneValue: PhoneNumberValue
  deeplinkUrl?: string
  className?: string
  handleJoinRoom: VoidFunction
  analyticsProps?: Record<string, string | undefined>
}

export const ShareScreenBase = ({
  renderContent,
  setPhoneValue,
  phoneValue,
  deeplinkUrl,
  className,
  handleJoinRoom,
  analyticsProps = {},
}: ShareScreenBaseProps): JSX.Element => {
  const [isHighlighted, setIsHighlighted] = useState(false)
  const { isKeyboardVisible } = useKeyboardVisibility()
  const phoneInputRef = useRef<HTMLInputElement>(null)
  const desktopPhoneInputRef = useRef<HTMLInputElement>(null)
  const hiddenPhoneInputHackRef = useRef<HTMLInputElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)

  const [footerHeight, setFooterHeight] = useState(0)

  useEffect(() => {
    const el = footerRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      setFooterHeight(
        entry.contentRect.height + (parseFloat(getComputedStyle(el).paddingBottom) || 0)
      )
    })
    observer.observe(el)
    setFooterHeight(el.getBoundingClientRect().height)
    return () => observer.disconnect()
  }, [])

  const autoDeeplinkRedirect = getMetaContentMaybe("auto-deeplink-redirect") === "true"
  useThemeColor("#0047FF")

  useEffect(() => {
    if (autoDeeplinkRedirect && deeplinkUrl && isMobile()) {
      window.location.href = deeplinkUrl
    }
  }, [autoDeeplinkRedirect, deeplinkUrl])

  const inviterData = getInviterData()
  const messageData = getMessageData()

  const handlePageClick = () => {
    if (!isMobile()) return
    setIsHighlighted(true)
    setTimeout(() => setIsHighlighted(false), 500)
  }

  /**
   * Manually focus the input for Safari so it doesn't fuck up our footer:
   *
   * Works around three issues:
   * 1. Safari doesn't honor fixed positioning with the keyboard up
   * 2 .Safari doesn't honor interactive-widget=resizes-content meta tag
   *    https://developer.chrome.com/blog/viewport-resize-behavior/
   * 2. iOS will auto-scroll for inputMode="tel" to provide auto-complete
   *
   * This crazy hack does the following:
   * 1. Prevents the default focus behavior
   * 2. Focuses a fake/hidden tel input first to bring up the keyboard
   * 3. Focuses the real input after setting type=number to avoid auto-complete issue
   * 4. Sets inputMode=tel which changes the keyboard without re-focusing the input
   */
  const handleManualFocus = (e: React.MouseEvent) => {
    if (isIOS()) {
      // On iOS, we need to prevent the default focus behavior
      // and handle it manually to avoid the automatic scroll
      e.preventDefault()

      if (
        phoneInputRef.current?.focus &&
        // If the input has a value, don't interfere
        (phoneInputRef.current?.value.length > 0 ||
          // If the hidden input is already in the viewport, avoid flicker
          (hiddenPhoneInputHackRef.current &&
            isKeyboardVisible &&
            hiddenPhoneInputHackRef.current.getBoundingClientRect().bottom <=
              (window.visualViewport?.height || 0)))
      ) {
        return
      }

      // Focus the input
      if (hiddenPhoneInputHackRef.current && phoneInputRef.current) {
        hiddenPhoneInputHackRef.current.focus()
      }
      setTimeout(() => {
        if (phoneInputRef.current && hiddenPhoneInputHackRef.current) {
          phoneInputRef.current.type = "number"
          phoneInputRef.current.inputMode = "number"
          phoneInputRef.current.focus()
          phoneInputRef.current.type = "tel"
          //hiddenPhoneInputHackRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 20)
      setTimeout(() => {
        if (phoneInputRef.current && hiddenPhoneInputHackRef.current) {
          phoneInputRef.current.inputMode = "tel" // setting type=tel would trigger re-focus
        }
      }, 100)
    }
  }

  return (
    <section
      className={cn(
        "ShareContent overflow-hidden overscroll-none bg-gradient-to-b from-[#0047FF] to-base-blue md:h-screen md:overflow-y-auto"
      )}
    >
      <TextureGrainBackground />
      <div
        className={cn(
          "relative flex flex-col overflow-auto overscroll-contain md:mb-0 md:min-h-screen md:justify-center md:overflow-visible",
          className
        )}
        style={
          footerHeight
            ? {
                height: `calc(100dvh - ${footerHeight}px)`,
                marginBottom: `${footerHeight}px`,
              }
            : undefined
        }
        onClick={handlePageClick}
      >
        <div className="flex w-full flex-col items-center justify-center gap-3 px-4 py-4 md:py-6">
          <Logo className="h-[40px]" analyticsProps={analyticsProps} />

          {/* Title and Description */}
          <div className="mx-auto flex flex-col items-center">
            <div className="mx-auto flex flex-col justify-center md:flex-row">
              <AppText
                variant="display"
                className="px-1 text-center text-[34px] leading-[34px] text-base-white md:text-[64px] md:leading-[64px]"
              >
                JOIN THE GROUP CHAT
              </AppText>
            </div>
          </div>
        </div>

        {/* Room Content Card */}
        <div className="flex flex-row justify-center">{renderContent()}</div>

        {/* Shared Message Section */}
        {messageData && (
          <div className={cn("flex flex-col items-center gap-6 px-4 py-6")}>
            <AppText variant="h1" className="text-center text-base-white">
              A message was shared with you
            </AppText>

            <div className="flex w-full max-w-[360px] rounded-[24px] bg-neutral-100 p-4">
              <ChatShareMessage message={messageData} />
            </div>
          </div>
        )}

        {/* Join Bar (Desktop) */}
        <div className="flex flex-col p-4 max-md:hidden">
          <JoinBar
            inviterData={inviterData}
            phoneNumberValue={phoneValue}
            setPhoneNumberValue={setPhoneValue}
            handleJoinRoom={handleJoinRoom}
            handleManualFocus={handleManualFocus}
            phoneInputRef={desktopPhoneInputRef}
          />
        </div>
      </div>

      {/*
       * Twitter's in-app browser injects scripts that detect and reposition fixed/sticky
       * footers so they don't collide with X's own bottom chrome (back button bar, share
       * controls). It stores original dimensions in data-original-* attributes and tags
       * modified elements with data-footer-adjusted. The result is that our fixed footer
       * bar gets pushed up by ~62 px. Adding this transparent spacer compensates for that
       * shift so the scrollable content area isn't clipped behind the footer.
       */}
      {isTwitterApp() && <div aria-hidden="true" className="h-[62px] bg-transparent md:hidden" />}

      {/* Footer Bar */}
      <div
        ref={footerRef}
        className={cn(
          "fixed bottom-0 left-0 right-0 touch-none overscroll-none px-4 transition-all duration-500 max-md:bg-base-black md:hidden",
          isHighlighted && "bg-support-purple duration-500 max-md:bg-support-purple",
          isIOS() && "pb-[env(safe-area-inset-bottom)]",
          isAndroid() && !isKeyboardVisible && "pb-[env(safe-area-inset-bottom)]"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Join Bar (Mobile) */}
        <JoinBar
          inviterData={inviterData}
          phoneNumberValue={phoneValue}
          setPhoneNumberValue={setPhoneValue}
          handleJoinRoom={handleJoinRoom}
          handleManualFocus={handleManualFocus}
          phoneInputRef={phoneInputRef}
        />
        <input
          ref={hiddenPhoneInputHackRef}
          type="tel"
          id="hidden-phone-number-input"
          name="hidden-phone-number-input"
          autoComplete="tel"
          className="fixed bottom-0 left-0 h-1 w-1 bg-transparent"
        />
      </div>
    </section>
  )
}
